import { Box, styled, Typography } from "@material-ui/core";
import type { CSSProperties, ReactNode } from "react";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PURPLE,
  EPOCUS_PURPLE_BG,
  EPOCUS_RED,
  EPOCUS_RED_BG,
  EPOCUS_STATUS_COMPLETE_BG,
  EPOCUS_STATUS_COMPLETE_TEXT,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { Card, MetaLabel } from "../../_lib/stat-primitives";
import { barWidthPct } from "./admin-metrics";

export const PageTitle: FC<{ title: string; subtitle: string }> = ({
  title,
  subtitle,
}) => (
  <Box>
    <Typography
      style={{
        fontSize: rem(22),
        fontWeight: 700,
        color: EPOCUS_TEXT_PRIMARY,
        lineHeight: 1.2,
      }}
    >
      {title}
    </Typography>
    <Typography
      style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY, marginTop: 4 }}
    >
      {subtitle}
    </Typography>
  </Box>
);

export const KpiGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
});

export const TwoColumn = styled(Box)({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 16,
  [epocusTheme.breakpoints.up("md")]: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  },
});

export const KpiCard: FC<{
  label: string;
  value: string;
  caption: string;
}> = ({ label, value, caption }) => (
  <Card>
    <MetaLabel>{label}</MetaLabel>
    <Typography
      style={{
        fontSize: rem(30),
        fontWeight: 700,
        lineHeight: 1.1,
        color: EPOCUS_TEXT_PRIMARY,
      }}
    >
      {value}
    </Typography>
    <Typography
      style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY, marginTop: 4 }}
    >
      {caption}
    </Typography>
  </Card>
);

export const CardHeading: FC<{ title: string; hint?: string }> = ({
  title,
  hint,
}) => (
  <Box
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 12,
    }}
  >
    <Typography
      style={{ fontSize: rem(15), fontWeight: 700, color: EPOCUS_TEXT_PRIMARY }}
    >
      {title}
    </Typography>
    {hint !== undefined && (
      <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
        {hint}
      </Typography>
    )}
  </Box>
);

export const CardNote: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY, marginTop: 10 }}
  >
    {children}
  </Typography>
);

const Track = styled(Box)({
  position: "relative",
  height: 8,
  flex: 1,
  minWidth: 60,
  borderRadius: 4,
  backgroundColor: EPOCUS_BG,
  overflow: "hidden",
});

export const ShareBar: FC<{ value: number | null; label: string }> = ({
  value,
  label,
}) => {
  const width = barWidthPct(value);
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 9 }}>
      {width !== null && (
        <Track>
          <Box
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${width}%`,
              borderRadius: 4,
              backgroundColor: EPOCUS_ICON_ACTIVE,
            }}
          />
        </Track>
      )}
      <Typography
        style={{
          fontSize: rem(13),
          fontWeight: 600,
          color: EPOCUS_TEXT_PRIMARY,
          minWidth: 44,
          textAlign: "right",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export type PillTone = "ok" | "warn" | "risk" | "info" | "neutral";

const PILL_TONES: Record<PillTone, { bg: string; text: string }> = {
  ok: { bg: EPOCUS_STATUS_REVIEWED_BG, text: EPOCUS_STATUS_REVIEWED_TEXT },
  warn: { bg: EPOCUS_STATUS_DRAFT_BG, text: EPOCUS_STATUS_DRAFT_TEXT },
  risk: { bg: EPOCUS_RED_BG, text: EPOCUS_RED },
  info: { bg: EPOCUS_PURPLE_BG, text: EPOCUS_PURPLE },
  neutral: { bg: EPOCUS_STATUS_COMPLETE_BG, text: EPOCUS_STATUS_COMPLETE_TEXT },
};

export const StatusPill: FC<{ tone: PillTone; label: string }> = ({
  tone,
  label,
}) => (
  <Box
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: rem(11),
      fontWeight: 700,
      whiteSpace: "nowrap",
      backgroundColor: PILL_TONES[tone].bg,
      color: PILL_TONES[tone].text,
    }}
  >
    {label}
  </Box>
);

export const TABLE_CELL_STYLE: CSSProperties = {
  padding: "10px 12px",
  textAlign: "left",
  verticalAlign: "middle",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  whiteSpace: "nowrap",
};

export const TABLE_HEAD_CELL_STYLE: CSSProperties = {
  ...TABLE_CELL_STYLE,
  fontSize: rem(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  backgroundColor: EPOCUS_BG,
};

export const ScrollTable: FC<{
  columns: readonly string[];
  children: ReactNode;
  minWidth?: number;
}> = ({ columns, children, minWidth = 640 }) => (
  <Box style={{ overflowX: "auto", margin: "0 -16px", padding: "0 16px" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th
              key={column === "" ? `spacer-${index}` : column}
              style={TABLE_HEAD_CELL_STYLE}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </Box>
);

export const EmptyRow: FC<{ colSpan: number; children: ReactNode }> = ({
  colSpan,
  children,
}) => (
  <tr>
    <td
      colSpan={colSpan}
      style={{
        ...TABLE_CELL_STYLE,
        textAlign: "center",
        color: EPOCUS_TEXT_SECONDARY,
        fontSize: rem(13),
        padding: "24px 12px",
        whiteSpace: "normal",
      }}
    >
      {children}
    </td>
  </tr>
);
